// components/downloadData/MyDownloads.jsx
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getMyDownloads,
  downloadExcelFile,
  checkBatchAvailability,
} from "../../Redux/ReduxSlices/downloadDataSlice";
import { toast } from "sonner";
import NormalLoader from "../customeLoader/NormalLoader";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import {
  Download,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Loader,
  DownloadCloud,
} from "lucide-react";
import DownloadDataFormPopup from "./DownloadDataFormPopup";

const MyDownloads = () => {
  const dispatch = useDispatch();
  const { myDownloads, downloadLoading, remainingDownloadLoading } =
    useSelector((state) => state.downloadData || {});
  const [isDownloadDataPopupOpen, setIsDownloadDataPopupOpen] = useState(false);
  const [remainingDownloadCount, setRemainingDownloadCount] = useState(0);
  const [remainingSessionId, setRemainingSessionId] = useState("");
  const [remainingBatchId, setRemainingBatchId] = useState("");
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [downloadingRemainingId, setDownloadingRemainingId] = useState(null);

  // Safe destructuring with fallbacks
  const {
    items = [],
    pagination = { page: 1, limit: 20, total: 0 },
    loading = false,
    error = null,
  } = myDownloads || {};

  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(20);
  const [hoveredPrompt, setHoveredPrompt] = useState(null);

  // Refs for polling
  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  // Function to check if there are any processing jobs
  // Function to check if there are any jobs that are not ready to download
  const hasPendingJobs = (itemsList) => {
    if (!itemsList || itemsList.length === 0) return false;
    return itemsList.some((item) =>
      item.jobs?.some((job) => job.is_ready_to_download === false),
    );
  };

  const isAllJobsReady = (jobs) => {
    if (!jobs || jobs.length === 0) return false;
    return jobs.every((job) => job.is_ready_to_download === true);
  };

  // Function to fetch downloads
  const fetchDownloads = async () => {
    await dispatch(getMyDownloads({ page: currentPage, limit }));
  };

  useEffect(() => {
    fetchDownloads();
  }, []);

  // Function to start polling
  // Function to start polling
  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    pollingIntervalRef.current = setInterval(() => {
      if (!loading && !isPollingRef.current) {
        isPollingRef.current = true;
        dispatch(getMyDownloads({ page: currentPage, limit })).finally(() => {
          isPollingRef.current = false;
        });
      } else {
      }
    }, 30000); // 30 seconds
  };

  // Function to stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  };

  useEffect(() => {
    const hasPending = hasPendingJobs(items);

    if (hasPending) {
      if (!pollingIntervalRef.current) {
        startPolling();
      }
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [items]);

  useEffect(() => {
    fetchDownloads();
    return () => {
      stopPolling();
    };
  }, [currentPage, limit]);

  const handleDownload = async (jobId, prompt) => {
    try {
      setDownloadingJobId(jobId);
      const fileName = `${(prompt || "download").slice(0, 30)}_${jobId.slice(0, 8)}.xlsx`;
      const result = await dispatch(downloadExcelFile({ jobId, fileName }));

      if (result.payload?.success) {
        toast.success("Data downloaded successfully!");
      } else if (result.error) {
        toast.error(result.error.message || "Failed to download file");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error(
        error.message || "An unexpected error occurred while downloading",
      );
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadRemaining = async (sessionId, batchId) => {
    setDownloadingRemainingId(`${sessionId}_${batchId}`);
    try {
      const response = await dispatch(
        checkBatchAvailability({ sessionId, batchId }),
      ).unwrap();
      if (response?.success) {
        setRemainingDownloadCount(response?.data?.remaining_available);
        setRemainingSessionId(sessionId);
        setRemainingBatchId(batchId);
        setIsDownloadDataPopupOpen(true);
      }
    } catch (error) {
      console.log("error", error);
      toast.error("Failed to check remaining availability");
    } finally {
      setDownloadingRemainingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const truncatePrompt = (prompt, maxLength = 60) => {
    if (!prompt) return "Untitled";
    if (prompt.length <= maxLength) return prompt;
    return prompt.slice(0, maxLength) + "...";
  };

  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return "N/A";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "USD",
      }).format(amount);
    } catch (error) {
      return `${currency || "USD"} ${amount}`;
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(
    (pagination.total || 0) / (pagination.limit || 20),
  );
  const startItem = (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, pagination.total || 0);

  // Show error if any
  if (error) {
    return (
      <div className="min-h-screen text-gray-900 flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-10 max-md:px-4 max-md:w-full">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center my-8">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-red-800 mb-2">
              Error Loading Downloads
            </h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchDownloads}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading && items.length === 0) {
    return (
      <div className="min-h-screen text-gray-900 flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-10 max-md:px-4 max-md:w-full flex items-center justify-center">
          <NormalLoader />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-900 flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-10 max-md:px-4 max-md:w-full">
        {/* Heading */}
        <div className="flex max-md:flex-col max-md:gap-3 justify-between items-center max-md:items-start p-4 max-md:p-2 max-md:pt-4 mb-6 max-md:mb-4">
          <div>
            <h4 className="text-2xl font-bold text-gray-900">My Downloads</h4>
            <p className="text-gray-600 text-sm">
              View and download your purchased email lists
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasPendingJobs(items) && (
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
                <Loader className="w-4 h-4 animate-spin text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">
                  Auto-refreshing...
                </span>
              </div>
            )}
            {items.length > 0 && (
              <div className="max-md:w-full flex justify-center max-md:hidden">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-lg">
                  <DownloadCloud className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-indigo-600 font-medium">
                    Total: {pagination.total || 0} downloads
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Summary - Reduced padding */}
        {items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">Total Downloads</p>
                  <p className="text-xl font-bold text-gray-900">
                    {pagination.total || 0}
                  </p>
                </div>
                <Download className="w-6 h-6 text-indigo-400" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">Total Sessions</p>
                  <p className="text-xl font-bold text-green-600">
                    {items.length}
                  </p>
                </div>
                <FileText className="w-6 h-6 text-green-400" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">Total Contacts</p>
                  <p className="text-xl font-bold text-purple-600">
                    {items
                      .reduce(
                        (sum, item) => sum + (item.total_emails_found || 0),
                        0,
                      )
                      .toLocaleString()}
                  </p>
                </div>
                <FileText className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </div>
        )}

        {/* Downloads Grid */}
        {items.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Downloads Yet
            </h3>
            <p className="text-gray-500 text-sm mb-6 max-w-md">
              Your purchased email lists will appear here once you complete a
              purchase.
            </p>
          </div>
        ) : (
          <>
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-5">
              {items.map((item) => {
                // Check if any job in this card is currently downloading
                const isCardDownloading = item.jobs?.some(
                  (job) => downloadingJobId === job.job_id,
                );

                return (
                  <div
                    key={item.session_id}
                    className="relative group bg-white rounded-lg shadow-sm border border-gray-200 hover:border-indigo-200 hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
                  >
                    {/* Full Card Overlay Loader */}
                    {isCardDownloading && (
                      <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-20 flex flex-col items-center justify-center gap-3">
                        <Loader className="w-10 h-10 animate-spin text-indigo-600" />
                        <span className="text-sm font-semibold text-gray-700">
                          Preparing download...
                        </span>
                        <span className="text-xs text-gray-500">
                          Please don't close this window
                        </span>
                      </div>
                    )}

                    {/* Card Header */}
                    <div className="p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div
                            className="relative"
                            onMouseEnter={() =>
                              setHoveredPrompt(item.session_id)
                            }
                            onMouseLeave={() => setHoveredPrompt(null)}
                          >
                            <h3 className="font-bold text-gray-900 text-base mb-1 line-clamp-1">
                              {truncatePrompt(item.prompt || "Untitled", 60)}
                            </h3>
                            {hoveredPrompt === item.session_id &&
                              (item.prompt || "").length > 60 && (
                                <div className="absolute z-30 mt-1 p-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg max-w-md whitespace-normal">
                                  {item.prompt}
                                </div>
                              )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(item.jobs?.[0]?.created_at)}
                            </span>
                            <span className="text-gray-300">|</span>
                            <span>
                              Total Available:{" "}
                              {(item.total_available || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          {item.display_status || "Ready"}
                        </div>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-4 space-y-3 relative z-10">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-gray-500">Requested</p>
                          <p className="font-semibold text-gray-900">
                            {(item.total_requested_count || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Emails Found</p>
                          <p className="font-semibold text-blue-600">
                            {(item.total_emails_found || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Total Amount</p>
                          <p className="font-semibold text-indigo-600">
                            {formatCurrency(item.total_amount, item.currency)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Remaining</p>
                          <p className="font-semibold text-orange-600">
                            {(item.remaining_available || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Download Slots */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-700">
                          Download Slots:
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {item.jobs?.map((job, index) => (
                            <button
                              key={job.job_id}
                              onClick={() =>
                                handleDownload(job.job_id, item.prompt)
                              }
                              disabled={
                                downloadingJobId === job.job_id ||
                                !job.is_ready_to_download ||
                                isCardDownloading
                              }
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {downloadingJobId === job.job_id ? (
                                <Loader className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Download className="w-3.5 h-3.5" />
                              )}
                              Slot {job.part_no || index + 1}
                              <span className="text-xs text-gray-500 ml-1">
                                ({job.emails_found?.toLocaleString()})
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Card Footer - Download Remaining Button */}
                    {item.remaining_available > 0 && (
                      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 relative z-10">
                        <button
                          onClick={() =>
                            handleDownloadRemaining(
                              item.session_id,
                              item.batch_id,
                            )
                          }
                          disabled={
                            downloadingRemainingId ===
                              `${item.session_id}_${item.batch_id}` ||
                            !isAllJobsReady(item.jobs) ||
                            isCardDownloading
                          }
                          className={`w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm ${
                            !isAllJobsReady(item.jobs)
                              ? "bg-gray-100 text-gray-700 cursor-not-allowed"
                              : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                          }`}
                        >
                          {downloadingRemainingId ===
                          `${item.session_id}_${item.batch_id}` ? (
                            <Loader className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          Download Remaining Data (
                          {item.remaining_available.toLocaleString()} available)
                          {!isAllJobsReady(item.jobs) && (
                            <span className="text-xs ml-1">
                              (Processing...)
                            </span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-5 flex-wrap gap-3">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startItem}</span> to{" "}
                  <span className="font-medium">{endItem}</span> of{" "}
                  <span className="font-medium">{pagination.total || 0}</span>{" "}
                  results
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = currentPage - 2 + idx;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 border rounded-md text-sm font-medium ${
                            currentPage === pageNum
                              ? "bg-indigo-600 text-white border-indigo-600"
                              : "border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <div className="pt-8">
        <Footer />
      </div>

      {isDownloadDataPopupOpen && (
        <DownloadDataFormPopup
          isOpen={isDownloadDataPopupOpen}
          onClose={() => setIsDownloadDataPopupOpen(false)}
          remainingDownload={true}
          remainingDownloadCount={remainingDownloadCount}
          remainingSessionId={remainingSessionId}
          remainingBatchId={remainingBatchId}
          remainingDownloadSuccess={() => {
            setIsDownloadDataPopupOpen(false);
            fetchDownloads();
          }}
        />
      )}
    </div>
  );
};

export default MyDownloads;
